/**
 * Carousel arrows that keep keyboard focus at either end (WCAG 2.4.3, 2.4.7).
 *
 * Swiper's navigation module sets `disabled` on a <button> arrow when a carousel
 * that doesn't loop reaches either end, and a focused button that becomes disabled
 * drops keyboard focus to <body> in Chrome and Safari: the Enter that reaches the
 * last slide leaves a keyboard user nowhere, and in the cart drawer outside the
 * drawer. Re-enabling the arrow from Swiper's toEdge / fromEdge events comes too
 * late in Chrome. Swiper's a11y module first writes tabindex on the still-disabled
 * arrow, and Chrome blurs a focused element on the spot when its tabindex changes
 * while it can't take focus.
 *
 * So the arrows never go to Swiper. Create the Swiper without `navigation`, then
 * hand the arrows to this helper:
 *
 *   var swiper = new Swiper(el, { ...everything but navigation... });
 *   window.JiYuSwiperArrows.wire(swiper, { prevEl: '.x-prev', nextEl: '.x-next' });
 *
 * prevEl / nextEl take what Swiper's navigation takes: an element, a list of them,
 * or a selector, which is looked up inside the carousel (or `root`) first and then
 * in the whole document. The arrows must be <button>s.
 *
 * An arrow at either end gets aria-disabled="true" and tabindex="-1", never
 * `disabled`. It leaves the Tab order as `disabled` took it out, but an arrow that
 * already has focus keeps it, and pressing it there does nothing. The other arrow
 * gets aria-disabled="false" and no tabindex. Style an end arrow with
 * [aria-disabled='true'] and dim its icon, not the button, or its focus ring dims
 * with it. When every slide fits (Swiper's watchOverflow lock) both arrows get
 * swiper-button-lock, which component-swiper.css hides.
 *
 * A pointer click that reaches an end lets go of focus, as `disabled` did: Chrome
 * focuses a clicked button, and the mouse user's next Space would press the dead
 * arrow instead of scrolling the page. Enter, Space and assistive technology click
 * with detail 0 and keep focus. Pass blurAtEnd: false where losing focus closes the
 * container, such as a <details> menu that closes on focusout; the clicked arrow
 * then keeps focus, and a press there does nothing.
 *
 * Also what Swiper's a11y module gave the arrows it was handed: aria-controls
 * pointing at the slides, and prevSlideMessage / nextSlideMessage as a name, but
 * only for an arrow the markup leaves unnamed (the module overwrote the markup's
 * own, localized label). The wiring comes off when the Swiper is destroyed; wiring
 * an arrow again first unwires it from the old Swiper. wire() returns { swiper,
 * update, destroy }, also kept on each arrow as __jiyuSwiperArrows, which names the
 * instance the arrows drive when a page puts two on one element. TASKS #161.
 */
(function () {
  'use strict';
  if (window.JiYuSwiperArrows) return;

  var EVENTS = 'afterInit toEdge fromEdge lock unlock enable disable';

  function toList(value, root) {
    if (!value) return [];
    if (typeof value === 'string') {
      var found = root ? root.querySelectorAll(value) : [];
      if (!found.length) found = document.querySelectorAll(value);
      return Array.prototype.slice.call(found);
    }
    if (value.nodeType === 1) return [value];
    return Array.prototype.filter.call(value, function (el) {
      return el && el.nodeType === 1;
    });
  }

  // Writes only on change, since this runs on every edge event.
  function setState(button, atEnd, locked, lockClass) {
    if (button.disabled) button.disabled = false;
    var aria = atEnd ? 'true' : 'false';
    if (button.getAttribute('aria-disabled') !== aria) button.setAttribute('aria-disabled', aria);
    if (atEnd) {
      if (button.getAttribute('tabindex') !== '-1') button.setAttribute('tabindex', '-1');
    } else if (button.hasAttribute('tabindex')) {
      button.removeAttribute('tabindex');
    }
    if (lockClass) button.classList.toggle(lockClass, locked);
  }

  function wire(swiper, options) {
    options = options || {};
    if (!swiper || swiper.destroyed) return null;
    var root = options.root || swiper.el;
    var prev = toList(options.prevEl, root);
    var next = toList(options.nextEl, root);
    var arrows = prev.concat(next);
    if (!arrows.length) return null;
    var lockClass = options.lockClass === undefined ? 'swiper-button-lock' : options.lockClass;
    var blurAtEnd = options.blurAtEnd !== false;

    arrows.forEach(function (button) {
      if (button.__jiyuSwiperArrows) button.__jiyuSwiperArrows.destroy();
    });

    var a11y = swiper.params.a11y;
    if (a11y && a11y.enabled !== false) {
      var controls = swiper.wrapperEl && swiper.wrapperEl.id;
      var name = function (buttons, message) {
        buttons.forEach(function (button) {
          if (controls) button.setAttribute('aria-controls', controls);
          if (message && !button.hasAttribute('aria-label') && !button.hasAttribute('aria-labelledby')) {
            button.setAttribute('aria-label', message);
          }
        });
      };
      name(prev, a11y.prevSlideMessage);
      name(next, a11y.nextSlideMessage);
    }

    var loops = function () {
      return !!(swiper.params.loop || swiper.params.rewind);
    };

    var update = function () {
      if (swiper.destroyed) return;
      var locked = !swiper.enabled || !!(swiper.params.watchOverflow && swiper.isLocked);
      prev.forEach(function (button) {
        setState(button, locked || (!loops() && swiper.isBeginning), locked, lockClass);
      });
      next.forEach(function (button) {
        setState(button, locked || (!loops() && swiper.isEnd), locked, lockClass);
      });
    };

    // Nothing happens at an end, as with Swiper's own handlers.
    var onClick = function (event) {
      var button = event.currentTarget;
      event.preventDefault();
      if (swiper.destroyed) return;
      if (prev.indexOf(button) !== -1) {
        if (loops() || !swiper.isBeginning) swiper.slidePrev();
      } else if (loops() || !swiper.isEnd) {
        swiper.slideNext();
      }
      if (blurAtEnd && event.detail !== 0 && document.activeElement === button && button.getAttribute('aria-disabled') === 'true') {
        button.blur();
      }
    };

    var controller;
    var unwire = function (swiperGoing) {
      arrows.forEach(function (button) {
        button.removeEventListener('click', onClick);
        if (lockClass) button.classList.remove(lockClass);
        if (button.__jiyuSwiperArrows === controller) delete button.__jiyuSwiperArrows;
      });
      // Swiper drops every listener itself after it emits 'destroy'.
      if (!swiperGoing && !swiper.destroyed) {
        swiper.off(EVENTS, update);
        swiper.off('destroy', onDestroy);
      }
    };
    var onDestroy = function () {
      unwire(true);
    };

    controller = {
      swiper: swiper,
      update: update,
      destroy: function () {
        unwire(false);
      },
    };
    arrows.forEach(function (button) {
      button.__jiyuSwiperArrows = controller;
      button.addEventListener('click', onClick);
    });
    swiper.on(EVENTS, update);
    swiper.on('destroy', onDestroy);
    update();
    return controller;
  }

  window.JiYuSwiperArrows = { wire: wire };
})();
